import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const AssetStatusChart = ({ data }) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const COLORS = {
    'In Use': '#10B981',
    'Available': '#F59E0B',
    'In Repair': '#EF4444',
    'Written-Off': '#6B7280',
    'Broken': '#DC2626',
    'Lost/Stolen': '#DC2626'
  };

  // --- NEW: Handle Empty State (Real Data Fix) ---
  if (!data || data.length === 0) {
    return (
        <div className="bg-card border border-border rounded-xl p-4 md:p-6 flex flex-col items-center justify-center h-64 sm:h-96 text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">{t('dashboard.status_chart_title')}</h3>
            <div className="bg-muted/30 rounded-full p-4 sm:p-6 mb-4">
                <span className="text-2xl sm:text-4xl">📊</span>
            </div>
            <p className="text-sm text-muted-foreground">{t('dashboard.no_assets_registered')}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{t('dashboard.add_asset_see_chart')}</p>
        </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0];
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-modal">
          <p className="font-medium text-popover-foreground">{data?.name}</p>
          <p className="text-sm text-muted-foreground">
            {t('dashboard.count')}: <span className="font-medium text-foreground">{data?.value}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            {/* The 'total' prop comes correctly from our new index.jsx logic */}
            {t('dashboard.percentage')}: <span className="font-medium text-foreground">{((data?.value / data?.payload?.total) * 100)?.toFixed(1)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
        {payload?.map((entry, index) => (
          <div key={index} className="flex items-center space-x-1.5">
            <div 
              className="w-2.5 h-2.5 rounded-full" 
              style={{ backgroundColor: entry?.color }}
            />
            <span className="text-xs sm:text-sm text-foreground">{entry?.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t('dashboard.status_chart_title')}</h3>
        <p className="text-sm text-muted-foreground">{t('dashboard.status_chart_subtitle')}</p>
      </div>
      <div className="h-64 sm:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={isMobile ? 45 : 60}
              outerRadius={isMobile ? 75 : 100}
              paddingAngle={2}
              dataKey="value"
            >
              {data?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS?.[entry?.name] || '#6B7280'} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AssetStatusChart;